import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import { lightTheme, darkTheme } from "../styles/theme";
import { techIcons } from "./Hero/techIcons";

const HeroSection = styled.section<{ theme: "light" | "dark" }>`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 80px 5% 40px;
  position: relative;
  overflow-x: hidden;
  overflow-y: hidden;
  background: ${({ theme }) =>
    theme === "light" ? "#ffffff" : darkTheme.background};

  /* Diagonal stripe pattern */
  background-image: ${({ theme }) =>
    theme === "light"
      ? `repeating-linear-gradient(
          45deg,
          transparent,
          transparent 35px,
          rgba(77, 159, 255, 0.12) 35px,
          rgba(77, 159, 255, 0.12) 70px
        )`
      : `repeating-linear-gradient(
          45deg,
          transparent,
          transparent 35px,
          rgba(107, 159, 255, 0.08) 35px,
          rgba(107, 159, 255, 0.08) 70px
        )`};

  /* Subtle accent overlay in corner */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 50%;
    height: 50%;
    background: ${({ theme }) =>
      theme === "light"
        ? `radial-gradient(ellipse at top right, rgba(0, 102, 255, 0.12), transparent 70%)`
        : `radial-gradient(ellipse at top right, ${darkTheme.accent}12, transparent 70%)`};
    pointer-events: none;
    z-index: 0;
  }

  @media (min-width: 768px) {
    padding: 70px 10% 40px;
  }
`;

const GeometricShape = styled(motion.div)<{
  theme: "light" | "dark";
  color: string;
  size: string;
  top: string;
  left: string;
}>`
  position: absolute;
  width: ${({ size }) => size};
  height: ${({ size }) => size};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  background: ${({ color }) => color};
  border: 5px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowLarge : darkTheme.shadowLarge};
  z-index: 1;
  pointer-events: none;
`;

const Square = styled(GeometricShape)`
  transform: rotate(15deg);
  opacity: 0.7;
`;

const Circle = styled(GeometricShape)`
  border-radius: 50%;
  opacity: 0.7;
`;

const FloatingIcons = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  opacity: 0.15;
`;

const TechIcon = styled(motion.div)<{
  theme: "light" | "dark";
  size: number;
  opacity: number;
}>`
  position: absolute;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.text : darkTheme.text};
  opacity: ${({ opacity }) => opacity};
  font-size: ${({ size }) => size}rem;
  z-index: 1;
  will-change: transform;
`;

const ContentWrapper = styled(motion.div)<{ theme: "light" | "dark" }>`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 16px 12px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.surface};
  border: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowLarge : darkTheme.shadowLarge};
  margin: 10px auto;
  display: flex;
  flex-direction: column;
  gap: 0;

  @media (min-width: 768px) {
    padding: 25px 28px;
  }

  @media (min-width: 1024px) {
    padding: 30px 35px;
    flex-direction: row;
    align-items: center;
    gap: 40px;
  }
`;

const ContentText = styled.div`
  flex: 1;
`;

const ImageWrapper = styled.div<{ theme: "light" | "dark" }>`
  display: none;

  @media (min-width: 1024px) {
    display: block;
    position: relative;
    width: 300px;
    height: 300px;
    flex-shrink: 0;
    border: 4px solid
      ${({ theme }) =>
        theme === "light" ? lightTheme.border : darkTheme.border};
    box-shadow: ${({ theme }) =>
      theme === "light" ? lightTheme.shadowSmall : darkTheme.shadowSmall};
    background: ${({ theme }) =>
      theme === "light" ? lightTheme.background : darkTheme.surface};
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  @media (min-width: 1200px) {
    width: 350px;
    height: 350px;
  }
`;

const TagLine = styled(motion.div)<{ theme: "light" | "dark" }>`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 6px 12px;
  margin-bottom: 12px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.accent : darkTheme.accent};
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  border: 3px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowSmall : darkTheme.shadowSmall};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-family: "Space Mono", monospace;
  transform: rotate(-2deg);

  @media (min-width: 768px) {
    font-size: 0.85rem;
  }
`;

const Title = styled(motion.h1)<{ theme: "light" | "dark" }>`
  font-size: 1.5rem;
  margin: 0 0 10px 0;
  font-weight: 900;
  line-height: 1.1;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.text : darkTheme.text};
  text-transform: uppercase;
  letter-spacing: -0.02em;

  @media (min-width: 768px) {
    font-size: 2.5rem;
    margin: 0 0 12px 0;
  }

  @media (min-width: 1024px) {
    font-size: 3rem;
  }
`;

const TitleHighlight = styled.span<{ theme: "light" | "dark"; color: string }>`
  display: inline-block;
  padding: 2px 6px;
  background: ${({ color }) => color};
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  border: 3px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadow : darkTheme.shadow};
  margin: 0 3px;
  transform: rotate(-2deg);
  transition: transform 0.3s ease;

  &:hover {
    transform: rotate(2deg) scale(1.05);
  }

  @media (min-width: 768px) {
    margin: 0 6px;
    padding: 3px 10px;
  }
`;

const Subtitle = styled(motion.h2)<{ theme: "light" | "dark" }>`
  font-size: 1rem;
  margin: 10px 0;
  padding-top: 16px;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.textSecondary : darkTheme.textSecondary};
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  @media (min-width: 768px) {
    font-size: 1.2rem;
  }

  @media (min-width: 1024px) {
    font-size: 1.4rem;
  }
`;

const Description = styled(motion.p)<{ theme: "light" | "dark" }>`
  font-size: 0.8rem;
  max-width: 600px;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.textSecondary : darkTheme.textSecondary};
  line-height: 1.5;
  margin: 8px 0 14px 0;
  font-weight: 500;

  @media (min-width: 768px) {
    font-size: 0.95rem;
    margin: 12px 0 20px 0;
  }
`;

const CTAButton = styled(motion.a)<{ theme: "light" | "dark" }>`
  display: inline-block;
  padding: 10px 18px;
  font-size: 0.75rem;
  font-weight: 900;
  text-transform: uppercase;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.accent : darkTheme.accent};
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  border: 3px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadow : darkTheme.shadow};
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.05em;

  &:hover {
    transform: translate(-3px, -3px);
    box-shadow: ${({ theme }) =>
      theme === "light" ? lightTheme.shadowLarge : darkTheme.shadowLarge};
  }

  &:active {
    transform: translate(0, 0);
    box-shadow: ${({ theme }) =>
      theme === "light" ? lightTheme.shadowSmall : darkTheme.shadowSmall};
  }

  @media (min-width: 768px) {
    padding: 10px 20px;
    font-size: 0.9rem;
  }

  @media (min-width: 1024px) {
    padding: 12px 24px;
    font-size: 1rem;
  }
`;

const StatsContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-top: 12px;
  width: 100%;

  @media (min-width: 768px) {
    display: flex;
    gap: 15px;
    margin-top: 20px;
    width: auto;
  }
`;

const StatBox = styled(motion.div)<{ theme: "light" | "dark"; color: string }>`
  padding: 8px 6px;
  background: ${({ color }) => color};
  border: 3px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowSmall : darkTheme.shadowSmall};
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  font-weight: 900;
  transform: rotate(-1deg);
  transition: transform 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 60px;

  &:hover {
    transform: rotate(1deg) scale(1.05);
  }

  @media (min-width: 768px) {
    padding: 8px 14px;
    flex: 0 1 auto;
    min-height: auto;
  }
`;

const StatNumber = styled.div`
  font-size: 0.95rem;
  line-height: 1.1;
  margin-bottom: 4px;
  font-family: "Space Mono", monospace;

  @media (min-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 3px;
  }
`;

const StatLabel = styled.div`
  font-size: 0.55rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  opacity: 0.9;
  line-height: 1.2;

  @media (min-width: 768px) {
    font-size: 0.7rem;
    letter-spacing: 0.05em;
  }
`;

const rotatingImages = [
  { src: "/icons/code.webp", alt: "Code" },
  { src: "/icons/me.webp", alt: "Trinh Van Thuan" },
  { src: "/icons/read.webp", alt: "Reading" },
  { src: "/icons/me.webp", alt: "Trinh Van Thuan" },
];

interface AnimatedImageProps {
  images: { src: string; alt: string }[];
  interval: number;
}

const AnimatedImage: React.FC<AnimatedImageProps> = ({ images, interval }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, interval);
    return () => clearInterval(timer);
  }, [images.length, interval]);

  return (
    <AnimatePresence mode="wait">
      <motion.img
        key={currentIndex}
        src={images[currentIndex].src}
        alt={images[currentIndex].alt}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.5 }}
        style={{
          width: "80%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          margin: "0 auto",
        }}
      />
    </AnimatePresence>
  );
};

const Hero: React.FC = () => {
  const { theme } = useTheme();
  const [titleIndex, setTitleIndex] = useState(0);
  const titles = ["Developer", "Engineer", "Builder"];

  useEffect(() => {
    const interval = setInterval(() => {
      setTitleIndex((prev) => (prev + 1) % titles.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [titles.length]);

  const handleScrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <HeroSection theme={theme} id="hero">
      {/* Floating Animated Shapes Background */}
      <Square
        theme={theme}
        color={theme === "light" ? lightTheme.accent : darkTheme.accent}
        size="80px"
        top="10%"
        left="75%"
        animate={{
          rotate: [0, 360],
          y: [0, -30, 0],
          x: [0, 15, 0],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <Circle
        theme={theme}
        color={theme === "light" ? lightTheme.accent : darkTheme.accent}
        size="60px"
        top="75%"
        left="78%"
        animate={{
          scale: [1, 1.3, 1],
          y: [0, 30, 0],
          x: [0, -20, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <Circle
        theme={theme}
        color={theme === "light" ? lightTheme.accent : darkTheme.accent}
        size="70px"
        top="20%"
        left="5%"
        animate={{
          scale: [1, 1.2, 1],
          y: [0, 25, 0],
          x: [0, 15, 0],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <Square
        theme={theme}
        color={theme === "light" ? lightTheme.accent : darkTheme.accent}
        size="50px"
        top="70%"
        left="8%"
        animate={{
          rotate: [0, -360],
          scale: [1, 1.2, 1],
          y: [0, -20, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <Circle
        theme={theme}
        color={theme === "light" ? lightTheme.accent : darkTheme.accent}
        size="40px"
        top="45%"
        left="82%"
        animate={{
          y: [0, -40, 0],
          x: [0, -15, 0],
          scale: [1, 0.8, 1],
        }}
        transition={{
          duration: 9,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Floating Tech Icons */}
      <FloatingIcons>
        {techIcons.map((icon, index) => {
          const IconComponent = icon.icon;
          return (
            <TechIcon
              key={index}
              theme={theme}
              size={icon.size || 2}
              opacity={icon.opacity || 0.3}
              initial={{
                x: icon.initialX,
                y: icon.initialY,
                rotate: 0,
              }}
              animate={{
                x: icon.animateX,
                y: icon.animateY,
                rotate: icon.rotate,
              }}
              transition={{
                duration: icon.duration,
                repeat: Infinity,
                ease: "linear",
                delay: index * 0.1,
              }}
              style={{
                top: icon.top,
                left: icon.left,
              }}
            >
              <IconComponent />
            </TechIcon>
          );
        })}
      </FloatingIcons>

      {/* Main Content */}
      <ContentWrapper
        theme={theme}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <ContentText>
          <TagLine
            theme={theme}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            ⚡ Full-Stack Developer
          </TagLine>

          <Title theme={theme}>
            Hi, I'm
            <br />
            <TitleHighlight
              theme={theme}
              color={theme === "light" ? lightTheme.accent : darkTheme.accent}
            >
              Trinh Van Thuan
            </TitleHighlight>
          </Title>

          <Subtitle theme={theme}>
            <AnimatePresence mode="wait">
              <motion.span
                key={titleIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                style={{
                  display: "inline-block",
                  color:
                    theme === "light" ? lightTheme.accent : darkTheme.accent,
                  fontWeight: 900,
                }}
              >
                {titles[titleIndex]}
              </motion.span>
            </AnimatePresence>
          </Subtitle>

          <Description theme={theme}>
            Building innovative solutions with <strong>blockchain</strong>,{" "}
            <strong>AI</strong>, and <strong>enterprise systems</strong>.
            Passionate about creating impactful software that makes a
            difference.
          </Description>

          <CTAButton
            theme={theme}
            onClick={handleScrollToContact}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Get In Touch →
          </CTAButton>

          <StatsContainer>
            <StatBox
              theme={theme}
              color={theme === "light" ? lightTheme.accent : darkTheme.accent}
              whileHover={{ scale: 1.05 }}
            >
              <StatNumber>5+</StatNumber>
              <StatLabel>Years Exp</StatLabel>
            </StatBox>
            <StatBox
              theme={theme}
              color={theme === "light" ? lightTheme.accent : darkTheme.accent}
              whileHover={{ scale: 1.05 }}
            >
              <StatNumber>AI</StatNumber>
              <StatLabel>Enthusiast</StatLabel>
            </StatBox>
            <StatBox
              theme={theme}
              color={theme === "light" ? lightTheme.accent : darkTheme.accent}
              whileHover={{ scale: 1.05 }}
            >
              <StatNumber>Product</StatNumber>
              <StatLabel>Builder</StatLabel>
            </StatBox>
          </StatsContainer>
        </ContentText>

        <ImageWrapper theme={theme}>
          <AnimatedImage images={rotatingImages} interval={3000} />
        </ImageWrapper>
      </ContentWrapper>
    </HeroSection>
  );
};

export default Hero;
